<script setup>
import DeleteCustomerAction from "@frontJs/components/actions/DeleteCustomerAction.vue";
import { router } from "@inertiajs/vue3";
import ActionButton from "@js/components/ActionButton.vue";
import InputField from "@js/components/fields/InputField.vue";
import ListboxField from "@js/components/fields/ListboxField.vue";
import SwitchField from "@js/components/fields/SwitchField.vue";
import WysiwygField from "@js/components/fields/WysiwygField.vue";
import SpinnerIcon from "@js/components/icons/SpinnerIcon.vue";
import TabGroup from "@js/components/tabs/TabGroup.vue";
import TabPanel from "@js/components/tabs/TabPanel.vue";
import Title from "@js/components/titles/Title.vue";
import { formatPrice } from "@js/utils/formatter";
import { goBack, useRoute } from "@js/utils/route";
import { Form } from "vee-validate";
import { reactive, ref } from "vue";
import { toast } from "vue3-toastify";
import { useI18n } from "vue-i18n";
import { array, boolean, number, object, string } from "yup";

const { t } = useI18n();

const types = [
  { value: "showcase", label: t("all.label.showcase") },
  { value: "e-commerce", label: t("all.label.eCommerce") },
];

const {
  tenant,
  seoPackages,
  seaPackages,
  smoPackages,
  smaPackages,
  semPackages,
  devPackages,
  hostingPackages,
  outsourcingPackages,
  cloudflarePackages,
  emailingPackages,
  shoppingfeedPackages,
  performanceStagePackages,
  performancePercentagePackages,
  specialPackages,
  moduleTree,
} = defineProps({
  tenant: {
    type: Object,
    required: false,
  },
  seoPackages: {
    type: Array,
    required: true,
  },
  seaPackages: {
    type: Array,
    required: true,
  },
  smoPackages: {
    type: Array,
    required: true,
  },
  smaPackages: {
    type: Array,
    required: true,
  },
  semPackages: {
    type: Array,
    required: true,
  },
  devPackages: {
    type: Array,
    required: true,
  },
  hostingPackages: {
    type: Array,
    required: true,
  },
  outsourcingPackages: {
    type: Array,
    required: true,
  },
  cloudflarePackages: {
    type: Array,
    required: true,
  },
  emailingPackages: {
    type: Array,
    required: true,
  },
  shoppingfeedPackages: {
    type: Array,
    required: true,
  },
  performanceStagePackages: {
    type: Array,
    required: true,
  },
  performancePercentagePackages: {
    type: Array,
    required: true,
  },
  specialPackages: {
    type: Array,
    required: true,
  },
  moduleTree: {
    type: Object,
    required: true,
  },
});

const { route } = useRoute();

const isSubmitting = ref(false);

const selectedTab = ref(0);

const form = reactive({
  type:
    tenant && tenant.type
      ? types.find((type) => type.value === tenant.type)
      : types[0],
  name: tenant ? tenant.name : "",
  url: tenant ? tenant.url : "",
  isArchived: tenant ? tenant.isArchived : false,
  displayOnDashboard: tenant ? tenant.displayOnDashboard : true,
  hasMonthlyReport: tenant ? tenant.hasMonthlyReport : true,
  hasInvoicing: tenant ? tenant.hasInvoicing : true,
  hasOpportunity: tenant ? tenant.hasOpportunity : true,
  hasApikomContent: tenant ? tenant.hasApikomContent : true,
  hasContentCategories: tenant ? tenant.hasContentCategories : true,
  hasContentProducts: tenant ? tenant.hasContentProducts : true,
  hasIndexation: tenant ? tenant.hasIndexation : true,
  hasInternalMesh: tenant ? tenant.hasInternalMesh : true,
  hasNetlinking: tenant ? tenant.hasNetlinking : true,
  hasPositionTracking: tenant ? tenant.hasPositionTracking : true,
  hasGoogleAdsAudience: tenant ? tenant.hasGoogleAdsAudience : true,
  hasFacebookAdsAudience: tenant ? tenant.hasFacebookAdsAudience : true,
  hasTenantTask: tenant ? tenant.hasTenantTask : true,
  contacts:
    tenant && tenant.contacts && tenant.contacts.length > 0
      ? tenant.contacts
      : [
          {
            id: null,
            name: "",
            email: "",
            phone: "",
          },
        ],
  serviceStartDate: tenant ? tenant.serviceStartDate : "",
  serviceEndDate: tenant ? tenant.serviceEndDate : "",
  displayTimeEveryMondayOfTheCurrentMonth: tenant
    ? tenant.displayTimeEveryMondayOfTheCurrentMonth
    : false,
  displayTimeOfTheCurrentMonth: tenant
    ? tenant.displayTimeOfTheCurrentMonth
    : false,
  seoPackage:
    tenant && tenant.seoPackage
      ? seoPackages.find((seoPackage) => seoPackage.id === tenant.seoPackage.id)
      : null,
  seaPackage:
    tenant && tenant.seaPackage
      ? seaPackages.find((seaPackage) => seaPackage.id === tenant.seaPackage.id)
      : null,
  smoPackage:
    tenant && tenant.smoPackage
      ? smoPackages.find((smoPackage) => smoPackage.id === tenant.smoPackage.id)
      : null,
  smaPackage:
    tenant && tenant.smaPackage
      ? smaPackages.find((smaPackage) => smaPackage.id === tenant.smaPackage.id)
      : null,
  semPackage:
    tenant && tenant.semPackage
      ? semPackages.find((semPackage) => semPackage.id === tenant.semPackage.id)
      : null,
  devPackage:
    tenant && tenant.devPackage
      ? devPackages.find((devPackage) => devPackage.id === tenant.devPackage.id)
      : null,
  hostingPackage:
    tenant && tenant.hostingPackage
      ? hostingPackages.find(
          (hostingPackage) => hostingPackage.id === tenant.hostingPackage.id,
        )
      : null,
  outsourcingPackage:
    tenant && tenant.outsourcingPackage
      ? outsourcingPackages.find(
          (outsourcingPackage) =>
            outsourcingPackage.id === tenant.outsourcingPackage.id,
        )
      : null,
  cloudflarePackage:
    tenant && tenant.cloudflarePackage
      ? cloudflarePackages.find(
          (cloudflarePackage) =>
            cloudflarePackage.id === tenant.cloudflarePackage.id,
        )
      : null,
  emailingPackage:
    tenant && tenant.emailingPackage
      ? emailingPackages.find(
          (emailingPackage) => emailingPackage.id === tenant.emailingPackage.id,
        )
      : null,
  shoppingfeedPackage:
    tenant && tenant.shoppingfeedPackage
      ? shoppingfeedPackages.find(
          (shoppingfeedPackage) =>
            shoppingfeedPackage.id === tenant.shoppingfeedPackage.id,
        )
      : null,
  performanceStagePackage:
    tenant && tenant.performanceStagePackage
      ? performanceStagePackages.find(
          (performanceStagePackage) =>
            performanceStagePackage.id === tenant.performanceStagePackage.id,
        )
      : null,
  performancePercentagePackage:
    tenant && tenant.performancePercentagePackage
      ? performancePercentagePackages.find(
          (performancePercentagePackage) =>
            performancePercentagePackage.id ===
            tenant.performancePercentagePackage.id,
        )
      : null,
  specialPackage:
    tenant && tenant.specialPackage
      ? specialPackages.find(
          (specialPackage) => specialPackage.id === tenant.specialPackage.id,
        )
      : null,
  emailing: tenant ? tenant.emailing : false,
  dev: tenant ? tenant.dev : false,
  devCoefficient: tenant ? tenant.devCoefficient : 1,
  freeCloudflare: tenant ? tenant.freeCloudflare : false,
  proCloudflare: tenant ? tenant.proCloudflare : false,
  service: tenant ? tenant.service : "",
  internalNotes: tenant ? tenant.internalNotes : "",
  seaBudgetPerMonth: tenant ? tenant.seaBudgetPerMonth : 0,
  smaBudgetPerMonth: tenant ? tenant.smaBudgetPerMonth : 0,
  maxBudgetPerMonth: tenant ? tenant.maxBudgetPerMonth : 0,
  roasTargetPercentage: tenant ? tenant.roasTargetPercentage : 0,
  grossMarginPercentage: tenant ? tenant.grossMarginPercentage : 0,
  budgetNotes: tenant ? tenant.budgetNotes : "",
  moduleTree: moduleTree,
  refreshDataTypes: {
    eCommerceStoreData: false,
    eCommerceLangData: false,
    eCommerceCustomerData: false,
    eCommerceCategoryData: false,
    eCommerceProductData: false,
    eCommerceProductAttributeData: false,
    eCommerceCartData: false,
    eCommerceOrderData: false,
    eCommerceOrderItemData: false,
    eCommerceInvoiceData: false,
    eCommerceShipmentData: false,
    eCommerceCreditMemoData: false,
    googleAdsDailyData: false,
    facebookAdsDailyData: false,
  },
  strategy: tenant ? tenant.strategy : "",
  numberOfStandardContentsPerMonth: tenant
    ? tenant.numberOfStandardContentsPerMonth
    : null,
  numberOfPremiumContentsPerMonth: tenant
    ? tenant.numberOfPremiumContentsPerMonth
    : null,
  numberOfBannersPerMonth: tenant ? tenant.numberOfBannersPerMonth : null,
  numberOfEmailingPerMonth: tenant ? tenant.numberOfEmailingPerMonth : null,
  numberOfInternalMeshPagesPerMonth: tenant
    ? tenant.numberOfInternalMeshPagesPerMonth
    : null,
  numberOfUserTestsPerQuarter: tenant
    ? tenant.numberOfUserTestsPerQuarter
    : null,
  hasEmailing: tenant ? tenant.hasEmailing : false,
  hasShortMessageService: tenant ? tenant.hasShortMessageService : false,
  hasPageSpeed: tenant ? tenant.hasPageSpeed : false,
  alertCategoryWithLessThanXProductsInStock: tenant
    ? tenant.alertCategoryWithLessThanXProductsInStock
    : null,
  displayApikomTaskTimeFrom: tenant ? tenant.displayApikomTaskTimeFrom : null,
});

const validationSchema = object({
  type: object().required(t("error.required")),
  name: string().required(t("error.required")),
  url: string().url(t("error.url")).required(t("error.required")),
  isArchived: boolean().required(t("error.required")),
  displayOnDashboard: boolean().required(t("error.required")),
  hasMonthlyReport: boolean().required(t("error.required")),
  hasInvoicing: boolean().required(t("error.required")),
  hasOpportunity: boolean().required(t("error.required")),
  hasApikomContent: boolean().required(t("error.required")),
  hasContentCategories: boolean().required(t("error.required")),
  hasContentProducts: boolean().required(t("error.required")),
  hasIndexation: boolean().required(t("error.required")),
  hasInternalMesh: boolean().required(t("error.required")),
  hasNetlinking: boolean().required(t("error.required")),
  hasPositionTracking: boolean().required(t("error.required")),
  hasGoogleAdsAudience: boolean().required(t("error.required")),
  hasFacebookAdsAudience: boolean().required(t("error.required")),
  hasTenantTask: boolean().required(t("error.required")),
  contacts: array().of(
    object({
      name: string().required(t("error.required")),
      email: string().email(t("error.email")).nullable(),
      phone: string().nullable(),
    }),
  ),
  serviceStartDate: string().nullable(),
  serviceEndDate: string().nullable(),
  displayTimeEveryMondayOfTheCurrentMonth: boolean().required(
    t("error.required"),
  ),
  displayTimeOfTheCurrentMonth: boolean().required(t("error.required")),
  seoPackage: object().nullable(),
  seaPackage: object().nullable(),
  smoPackage: object().nullable(),
  smaPackage: object().nullable(),
  semPackage: object().nullable(),
  devPackage: object().nullable(),
  hostingPackage: object().nullable(),
  outsourcingPackage: object().nullable(),
  cloudflarePackage: object().nullable(),
  emailingPackage: object().nullable(),
  shoppingfeedPackage: object().nullable(),
  performanceStagePackage: object().nullable(),
  performancePercentagePackage: object().nullable(),
  specialPackage: object().nullable(),
  emailing: boolean().nullable(),
  dev: boolean().nullable(),
  devCoefficient: number().nullable(),
  freeCloudflare: boolean().nullable(),
  proCloudflare: boolean().nullable(),
  service: string().nullable(),
  internalNotes: string().nullable(),
  seaBudgetPerMonth: number().nullable(),
  smaBudgetPerMonth: number().nullable(),
  maxBudgetPerMonth: number().nullable(),
  roasTargetPercentage: number().nullable(),
  grossMarginPercentage: number().nullable(),
  budgetNotes: string().nullable(),
  strategy: string().nullable(),
  numberOfStandardContentsPerMonth: number().nullable(),
  numberOfPremiumContentsPerMonth: number().nullable(),
  numberOfBannersPerMonth: number().nullable(),
  numberOfEmailingPerMonth: number().nullable(),
  numberOfInternalMeshPagesPerMonth: number().nullable(),
  numberOfUserTestsPerQuarter: number().nullable(),
  hasEmailing: boolean().required(t("error.required")),
  hasShortMessageService: boolean().required(t("error.required")),
  hasPageSpeed: boolean().required(t("error.required")),
  alertCategoryWithLessThanXProductsInStock: number().nullable(),
  displayApikomTaskTimeFrom: string().nullable(),
});

function changeTab(index) {
  selectedTab.value = index;
}

function saveCustomer() {
  isSubmitting.value = true;

  fetch(
    route(
      "front.projectManagement.customer.postCustomer",
      tenant ? { id: tenant.id } : {},
    ),
    {
      method: "POST",
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
        "X-CSRF-TOKEN": document
          .querySelector('meta[name="csrf-token"]')
          .getAttribute("content"),
      },
      body: JSON.stringify({
        type: form.type.value,
        name: form.name,
        url: form.url,
        isArchived: form.isArchived,
        displayOnDashboard: form.displayOnDashboard,
        hasMonthlyReport: form.hasMonthlyReport,
        hasInvoicing: form.hasInvoicing,
        hasOpportunity: form.hasOpportunity,
        hasApikomContent: form.hasApikomContent,
        hasContentCategories: form.hasContentCategories,
        hasContentProducts: form.hasContentProducts,
        hasIndexation: form.hasIndexation,
        hasInternalMesh: form.hasInternalMesh,
        hasNetlinking: form.hasNetlinking,
        hasPositionTracking: form.hasPositionTracking,
        hasGoogleAdsAudience: form.hasGoogleAdsAudience,
        hasFacebookAdsAudience: form.hasFacebookAdsAudience,
        hasTenantTask: form.hasTenantTask,
        contacts: form.contacts,
        serviceStartDate: form.serviceStartDate,
        serviceEndDate: form.serviceEndDate,
        displayTimeEveryMondayOfTheCurrentMonth:
          form.displayTimeEveryMondayOfTheCurrentMonth,
        displayTimeOfTheCurrentMonth: form.displayTimeOfTheCurrentMonth,
        seoPackageId: form.seoPackage ? form.seoPackage.id : null,
        seaPackageId: form.seaPackage ? form.seaPackage.id : null,
        smoPackageId: form.smoPackage ? form.smoPackage.id : null,
        smaPackageId: form.smaPackage ? form.smaPackage.id : null,
        semPackageId: form.semPackage ? form.semPackage.id : null,
        devPackageId: form.devPackage ? form.devPackage.id : null,
        hostingPackageId: form.hostingPackage ? form.hostingPackage.id : null,
        outsourcingPackageId: form.outsourcingPackage
          ? form.outsourcingPackage.id
          : null,
        cloudflarePackageId: form.cloudflarePackage
          ? form.cloudflarePackage.id
          : null,
        emailingPackageId: form.emailingPackage
          ? form.emailingPackage.id
          : null,
        shoppingfeedPackageId: form.shoppingfeedPackage
          ? form.shoppingfeedPackage.id
          : null,
        performanceStagePackageId: form.performanceStagePackage
          ? form.performanceStagePackage.id
          : null,
        performancePercentagePackageId: form.performancePercentagePackage
          ? form.performancePercentagePackage.id
          : null,
        specialPackageId: form.specialPackage ? form.specialPackage.id : null,
        emailing: form.emailing,
        dev: form.dev,
        devCoefficient: form.devCoefficient,
        freeCloudflare: form.freeCloudflare,
        proCloudflare: form.proCloudflare,
        service: form.service,
        internalNotes: form.internalNotes,
        seaBudgetPerMonth: form.seaBudgetPerMonth,
        smaBudgetPerMonth: form.smaBudgetPerMonth,
        maxBudgetPerMonth: form.maxBudgetPerMonth,
        roasTargetPercentage: form.roasTargetPercentage,
        grossMarginPercentage: form.grossMarginPercentage,
        budgetNotes: form.budgetNotes,
        moduleTree: form.moduleTree,
        strategy: form.strategy,
        numberOfStandardContentsPerMonth: form.numberOfStandardContentsPerMonth,
        numberOfPremiumContentsPerMonth: form.numberOfPremiumContentsPerMonth,
        numberOfBannersPerMonth: form.numberOfBannersPerMonth,
        numberOfEmailingPerMonth: form.numberOfEmailingPerMonth,
        numberOfInternalMeshPagesPerMonth:
          form.numberOfInternalMeshPagesPerMonth,
        numberOfUserTestsPerQuarter: form.numberOfUserTestsPerQuarter,
        hasEmailing: form.hasEmailing,
        hasShortMessageService: form.hasShortMessageService,
        hasPageSpeed: form.hasPageSpeed,
        alertCategoryWithLessThanXProductsInStock:
          form.alertCategoryWithLessThanXProductsInStock,
        displayApikomTaskTimeFrom: form.displayApikomTaskTimeFrom,
      }),
    },
  )
    .then((response) => {
      if (!response.ok) {
        throw new Error(response.statusText);
      }

      return response.json();
    })
    .then((data) => {
      toast.success(t("toast.successfullySaved"), {
        position: "top-right",
      });

      if (tenant) {
        router.visit(
          route("front.projectManagement.customer.updateCustomer", {
            id: data.id,
          }),
        );
      } else {
        goBack();
      }
    })
    .finally(() => {
      isSubmitting.value = false;
    })
    .catch((error) => {
      toast.error(error.message, { position: "top-right" });
      router.visit(route("front.projectManagement.customer.contacts"));
    });
}

function deleteCallback() {
  toast.success(t("toast.successfullyDeleted"), { position: "top-right" });
  router.visit(route("front.projectManagement.customer.contacts"), {
    preserveState: true,
    replace: true,
  });
}
</script>

<template>
  <Form
    :validation-schema="validationSchema"
    @submit="saveCustomer"
    v-slot="{ errors }"
  >
    <TabGroup
      :selected-tab="selectedTab"
      :change-tab="changeTab"
      :tabs="{
        information: t('title.information'),
        services: t('title.services'),
        budgets: t('title.budgets'),
        strategy: t('title.strategy'),
        apikomObjectives: t('title.apikomObjectives'),
        internalNotes: t('title.internalNotes'),
        display: t('title.display'),
        modules: t('title.modules'),
        alerts: t('title.alerts'),
      }"
      :replace="false"
    >
      <template #panels>
        <TabPanel>
          <div class="grid grid-cols-1 gap-x-8 md:grid-cols-2 lg:grid-cols-4">
            <ListboxField
              v-model="form.type"
              field-id="type"
              field-name="type"
              :label="t('label.type')"
              :options="types"
              required
            >
              <template #buttonContent>
                {{ form.type.label }}
              </template>

              <template #optionContent="{ option }">
                {{ option.label }}
              </template>
            </ListboxField>

            <InputField
              v-model="form.name"
              :label="t('label.name')"
              field-name="name"
              field-id="name"
              type="text"
              required
            />

            <InputField
              v-model="form.url"
              :label="t('label.url')"
              field-name="url"
              field-id="url"
              type="url"
              required
            />
          </div>

          <hr class="h-0.25 my-4 w-full bg-gray-200" />

          <div class="grid grid-cols-1 gap-x-8 md:grid-cols-2 lg:grid-cols-4">
            <SwitchField
              v-model="form.isArchived"
              :label="t('label.archived')"
              field-name="isArchived"
              field-id="isArchived"
            />

            <SwitchField
              v-model="form.displayOnDashboard"
              :label="t('all.label.displayOnDashboard')"
              field-name="displayOnDashboard"
              field-id="displayOnDashboard"
            />
          </div>

          <hr class="h-0.25 my-4 w-full bg-gray-200" />

          <div
            v-for="(contact, index) in form.contacts"
            :key="index"
            class="grid grid-cols-1 gap-x-8 md:grid-cols-2 lg:grid-cols-4"
          >
            <InputField
              v-model="form.contacts[index].name"
              :label="t('all.label.contactName', { index: index + 1 })"
              :field-name="'contacts[' + index + '].name'"
              :field-id="'contacts[' + index + '].name'"
              type="text"
              required
            />

            <InputField
              v-model="contact.email"
              :label="t('all.label.contactEmail', { index: index + 1 })"
              :field-name="'contacts[' + index + '].email'"
              :field-id="'contacts[' + index + '].email'"
              type="email"
            />

            <InputField
              v-model="contact.phone"
              :label="t('all.label.contactPhone', { index: index + 1 })"
              :field-name="'contacts[' + index + '].phone'"
              :field-id="'contacts[' + index + '].phone'"
              type="tel"
            />

            <div class="mb-4 flex items-end justify-start">
              <ActionButton :callback="() => form.contacts.splice(index, 1)">
                {{ t("all.button.delete") }}
              </ActionButton>
            </div>
          </div>

          <ActionButton
            :callback="
              () =>
                form.contacts.push({ id: null, name: '', email: '', phone: '' })
            "
          >
            {{ t("button.add") }}
          </ActionButton>
        </TabPanel>

        <TabPanel>
          <div class="grid grid-cols-1 gap-x-8 md:grid-cols-2 lg:grid-cols-4">
            <InputField
              v-model="form.serviceStartDate"
              :label="t('all.label.serviceStartDate')"
              field-name="serviceStartDate"
              field-id="serviceStartDate"
              type="date"
            />

            <InputField
              v-model="form.serviceEndDate"
              :label="t('all.label.serviceEndDate')"
              field-name="serviceEndDate"
              field-id="serviceEndDate"
              type="date"
            />

            <InputField
              v-model="form.displayApikomTaskTimeFrom"
              :label="t('label.displayApikomTaskTimeFrom')"
              field-name="displayApikomTaskTimeFrom"
              field-id="displayApikomTaskTimeFrom"
              type="date"
            />
          </div>

          <hr class="h-0.25 my-4 w-full bg-gray-200" />

          <div class="grid grid-cols-1 gap-x-8 md:grid-cols-2 lg:grid-cols-4">
            <ListboxField
              v-model="form.seoPackage"
              field-id="seoPackage"
              field-name="seoPackage"
              :label="t('all.label.seoPackage')"
              :options="seoPackages"
              has-empty-option
              by="id"
            >
              <template #buttonContent>
                {{ form.seoPackage.name }}
              </template>

              <template #optionContent="{ option }">
                {{ option.name }}
              </template>
            </ListboxField>

            <ListboxField
              v-model="form.seaPackage"
              field-id="seaPackage"
              field-name="seaPackage"
              :label="t('all.label.seaPackage')"
              :options="seaPackages"
              has-empty-option
              by="id"
            >
              <template #buttonContent>
                {{ form.seaPackage.name }} ({{
                  formatPrice(form.seaPackage.price, false)
                }})
              </template>

              <template #optionContent="{ option }">
                {{ option.name }} ({{ formatPrice(option.price, false) }})
              </template>
            </ListboxField>

            <ListboxField
              v-model="form.smoPackage"
              field-id="smoPackage"
              field-name="smoPackage"
              :label="t('all.label.smoPackage')"
              :options="smoPackages"
              has-empty-option
              by="id"
            >
              <template #buttonContent>
                {{ form.smoPackage.name }}
              </template>

              <template #optionContent="{ option }">
                {{ option.name }}
              </template>
            </ListboxField>

            <ListboxField
              v-model="form.smaPackage"
              field-id="smaPackage"
              field-name="smaPackage"
              :label="t('all.label.smaPackage')"
              :options="smaPackages"
              has-empty-option
              by="id"
            >
              <template #buttonContent>
                {{ form.smaPackage.name }} ({{
                  formatPrice(form.smaPackage.price, false)
                }})
              </template>

              <template #optionContent="{ option }">
                {{ option.name }} ({{ formatPrice(option.price, false) }})
              </template>
            </ListboxField>

            <ListboxField
              v-model="form.semPackage"
              field-id="semPackage"
              field-name="semPackage"
              :label="t('all.label.semPackage')"
              :options="semPackages"
              has-empty-option
              by="id"
            >
              <template #buttonContent>
                {{ form.semPackage.name }}
              </template>

              <template #optionContent="{ option }">
                {{ option.name }}
              </template>
            </ListboxField>

            <ListboxField
              v-model="form.emailingPackage"
              field-id="emailingPackage"
              field-name="emailingPackage"
              :label="t('all.label.emailingPackage')"
              :options="emailingPackages"
              has-empty-option
              by="id"
            >
              <template #buttonContent>
                {{ form.emailingPackage.name }}
              </template>

              <template #optionContent="{ option }">
                {{ option.name }}
              </template>
            </ListboxField>

            <ListboxField
              v-model="form.shoppingfeedPackage"
              field-id="shoppingfeedPackage"
              field-name="shoppingfeedPackage"
              :label="t('all.label.shoppingfeedPackage')"
              :options="shoppingfeedPackages"
              has-empty-option
              by="id"
            >
              <template #buttonContent>
                {{ form.shoppingfeedPackage.name }}
              </template>

              <template #optionContent="{ option }">
                {{ option.name }}
              </template>
            </ListboxField>
          </div>

          <div class="grid grid-cols-1 gap-x-8 md:grid-cols-2 lg:grid-cols-4">
            <ListboxField
              v-model="form.devPackage"
              field-id="devPackage"
              field-name="devPackage"
              :label="t('all.label.devPackage')"
              :options="devPackages"
              has-empty-option
              by="id"
            >
              <template #buttonContent>
                {{ form.devPackage.name }}
              </template>

              <template #optionContent="{ option }">
                {{ option.name }}
              </template>
            </ListboxField>

            <ListboxField
              v-model="form.hostingPackage"
              field-id="hostingPackage"
              field-name="hostingPackage"
              :label="t('all.label.hostingPackage')"
              :options="hostingPackages"
              has-empty-option
              by="id"
            >
              <template #buttonContent>
                {{ form.hostingPackage.name }}
              </template>

              <template #optionContent="{ option }">
                {{ option.name }}
              </template>
            </ListboxField>

            <ListboxField
              v-model="form.outsourcingPackage"
              field-id="outsourcingPackage"
              field-name="outsourcingPackage"
              :label="t('all.label.outsourcingPackage')"
              :options="outsourcingPackages"
              has-empty-option
              by="id"
            >
              <template #buttonContent>
                {{ form.outsourcingPackage.name }}
              </template>

              <template #optionContent="{ option }">
                {{ option.name }}
              </template>
            </ListboxField>

            <ListboxField
              v-model="form.cloudflarePackage"
              field-id="cloudflarePackage"
              field-name="cloudflarePackage"
              :label="t('all.label.cloudflarePackage')"
              :options="cloudflarePackages"
              has-empty-option
              by="id"
            >
              <template #buttonContent>
                {{ form.cloudflarePackage.name }}
              </template>

              <template #optionContent="{ option }">
                {{ option.name }}
              </template>
            </ListboxField>
          </div>

          <div class="grid grid-cols-1 gap-x-8 md:grid-cols-2 lg:grid-cols-4">
            <ListboxField
              v-model="form.performanceStagePackage"
              field-id="performanceStagePackage"
              field-name="performanceStagePackage"
              :label="t('all.label.performanceStagePackage')"
              :options="performanceStagePackages"
              has-empty-option
              by="id"
            >
              <template #buttonContent>
                {{ form.performanceStagePackage.name }}
              </template>

              <template #optionContent="{ option }">
                {{ option.name }}
              </template>
            </ListboxField>

            <ListboxField
              v-model="form.performancePercentagePackage"
              field-id="performancePercentagePackage"
              field-name="performancePercentagePackage"
              :label="t('all.label.performancePercentagePackage')"
              :options="performancePercentagePackages"
              has-empty-option
            >
              <template #buttonContent>
                {{ form.performancePercentagePackage.name }}
              </template>

              <template #optionContent="{ option }">
                {{ option.name }}
              </template>
            </ListboxField>

            <ListboxField
              v-model="form.specialPackage"
              field-id="specialPackage"
              field-name="specialPackage"
              :label="t('all.label.specialPackage')"
              :options="specialPackages"
              has-empty-option
              by="id"
            >
              <template #buttonContent>
                {{ form.specialPackage.name }}
              </template>

              <template #optionContent="{ option }">
                {{ option.name }}
              </template>
            </ListboxField>
          </div>

          <hr class="h-0.25 my-4 w-full bg-gray-200" />

          <div class="grid grid-cols-1 gap-x-8 md:grid-cols-2 lg:grid-cols-4">
            <SwitchField
              v-model="form.emailing"
              :label="t('all.label.emailing')"
              field-name="emailing"
              field-id="emailing"
            />

            <SwitchField
              v-model="form.dev"
              :label="t('all.label.dev')"
              field-name="dev"
              field-id="dev"
            />

            <SwitchField
              v-model="form.freeCloudflare"
              :label="t('label.freeCloudflare')"
              field-name="freeCloudflare"
              field-id="freeCloudflare"
            />

            <SwitchField
              v-model="form.proCloudflare"
              :label="t('label.proCloudflare')"
              field-name="proCloudflare"
              field-id="proCloudflare"
            />
          </div>

          <hr class="h-0.25 my-4 w-full bg-gray-200" />

          <div class="grid grid-cols-1 gap-x-8 md:grid-cols-2 lg:grid-cols-4">
            <InputField
              v-model="form.devCoefficient"
              :label="t('all.label.devCoefficient')"
              field-name="devCoefficient"
              field-id="devCoefficient"
              type="number"
              :step="0.01"
              :min="0"
            />
          </div>
        </TabPanel>

        <TabPanel>
          <div class="grid grid-cols-1 gap-x-8 md:grid-cols-2 lg:grid-cols-4">
            <InputField
              v-model="form.seaBudgetPerMonth"
              :label="t('all.label.seaBudgetPerMonth')"
              field-name="seaBudgetPerMonth"
              field-id="seaBudgetPerMonth"
              type="number"
              min="0"
              step="0.01"
            />

            <InputField
              v-model="form.smaBudgetPerMonth"
              :label="t('all.label.smaBudgetPerMonth')"
              field-name="smaBudgetPerMonth"
              field-id="smaBudgetPerMonth"
              type="number"
              min="0"
              step="0.01"
            />

            <InputField
              v-model="form.maxBudgetPerMonth"
              :label="t('label.maxBudgetPerMonth')"
              field-name="maxBudgetPerMonth"
              field-id="maxBudgetPerMonth"
              type="number"
              min="0"
              step="0.01"
            />
          </div>

          <div class="grid grid-cols-1 gap-x-8 md:grid-cols-2 lg:grid-cols-4">
            <InputField
              v-model="form.roasTargetPercentage"
              :label="t('all.label.roasTargetPercentage')"
              field-name="roasTargetPercentage"
              field-id="roasTargetPercentage"
              type="number"
              min="0"
              step="0.01"
            />
          </div>

          <div class="grid grid-cols-1 gap-x-8 md:grid-cols-2 lg:grid-cols-4">
            <InputField
              v-model="form.grossMarginPercentage"
              :label="t('all.label.grossMarginPercentage')"
              field-name="grossMarginPercentage"
              field-id="grossMarginPercentage"
              type="number"
              min="0"
              step="0.01"
            />
          </div>

          <hr class="h-0.25 my-4 w-full bg-gray-200" />

          <WysiwygField
            v-model="form.budgetNotes"
            :label="t('label.budgetNotes')"
            field-name="budgetNotes"
            field-id="budgetNotes"
          />
        </TabPanel>

        <TabPanel>
          <WysiwygField
            id="strategy"
            v-model="form.strategy"
            :label="t('label.strategy')"
            field-name="strategy"
            field-id="strategy"
          />
        </TabPanel>

        <TabPanel>
          <Title>{{ t("title.seo") }}</Title>

          <div class="grid grid-cols-1 gap-x-8 md:grid-cols-2 lg:grid-cols-4">
            <InputField
              v-model="form.numberOfStandardContentsPerMonth"
              :label="t('label.numberOfStandardContentsPerMonth')"
              field-name="numberOfStandardContentsPerMonth"
              field-id="numberOfStandardContentsPerMonth"
              type="number"
              min="0"
              step="1"
            />

            <InputField
              v-model="form.numberOfPremiumContentsPerMonth"
              :label="t('label.numberOfPremiumContentsPerMonth')"
              field-name="numberOfPremiumContentsPerMonth"
              field-id="numberOfPremiumContentsPerMonth"
              type="number"
              min="0"
              step="1"
            />

            <InputField
              v-model="form.numberOfInternalMeshPagesPerMonth"
              :label="t('label.numberOfInternalMeshPagesPerMonth')"
              field-name="numberOfInternalMeshPagesPerMonth"
              field-id="numberOfInternalMeshPagesPerMonth"
              type="number"
              min="0"
              step="1"
            />
          </div>

          <Title>{{ t("title.sem") }}</Title>

          <div class="grid grid-cols-1 gap-x-8 md:grid-cols-2 lg:grid-cols-4">
            <InputField
              v-model="form.numberOfBannersPerMonth"
              :label="t('label.numberOfBannersPerMonth')"
              field-name="numberOfBannersPerMonth"
              field-id="numberOfBannersPerMonth"
              type="number"
              min="0"
              step="1"
            />

            <InputField
              v-model="form.numberOfEmailingPerMonth"
              :label="t('label.numberOfEmailingPerMonth')"
              field-name="numberOfEmailingPerMonth"
              field-id="numberOfEmailingPerMonth"
              type="number"
              min="0"
              step="1"
            />
          </div>

          <Title>{{ t("title.cs") }}</Title>

          <div class="grid grid-cols-1 gap-x-8 md:grid-cols-2 lg:grid-cols-4">
            <InputField
              v-model="form.numberOfUserTestsPerQuarter"
              :label="t('label.numberOfUserTestsPerQuarter')"
              field-name="numberOfUserTestsPerQuarter"
              field-id="numberOfUserTestsPerQuarter"
              type="number"
              min="0"
              step="1"
            />
          </div>
        </TabPanel>

        <TabPanel>
          <WysiwygField
            id="internalNotes"
            v-model="form.internalNotes"
            :label="t('all.label.internalNotes')"
            field-name="internalNotes"
            field-id="internalNotes"
          />
        </TabPanel>

        <TabPanel>
          <Title>{{ t("title.tasks") }}</Title>

          <SwitchField
            v-model="form.displayTimeEveryMondayOfTheCurrentMonth"
            :label="t('all.label.displayTimeEveryMondayOfTheCurrentMonth')"
            field-name="displayTimeEveryMondayOfTheCurrentMonth"
            field-id="displayTimeEveryMondayOfTheCurrentMonth"
          />

          <SwitchField
            v-model="form.displayTimeOfTheCurrentMonth"
            :label="t('label.displayTimeOfTheCurrentMonth')"
            field-name="displayTimeOfTheCurrentMonth"
            field-id="displayTimeOfTheCurrentMonth"
          />

          <SwitchField
            v-model="form.hasTenantTask"
            :label="t('label.hasTenantTask')"
            field-name="hasTenantTask"
            field-id="hasTenantTask"
          />

          <Title>{{ t("title.apikom") }}</Title>

          <SwitchField
            v-model="form.hasInvoicing"
            :label="t('label.hasInvoicing')"
            field-name="hasInvoicing"
            field-id="hasInvoicing"
          />

          <SwitchField
            v-model="form.hasOpportunity"
            :label="t('label.hasOpportunity')"
            field-name="hasOpportunity"
            field-id="hasOpportunity"
          />

          <Title>{{ t("title.customers") }}</Title>

          <SwitchField
            v-model="form.hasMonthlyReport"
            :label="t('label.hasMonthlyReport')"
            field-name="hasMonthlyReport"
            field-id="hasMonthlyReport"
          />

          <Title>{{ t("title.dev") }}</Title>

          <SwitchField
            v-model="form.hasPageSpeed"
            :label="t('label.hasPageSpeed')"
            field-name="hasPageSpeed"
            field-id="hasPageSpeed"
          />

          <Title>{{ t("title.sea") }}</Title>

          <SwitchField
            v-model="form.hasGoogleAdsAudience"
            :label="t('label.hasGoogleAdsAudience')"
            field-name="hasGoogleAdsAudience"
            field-id="hasGoogleAdsAudience"
          />

          <Title>{{ t("title.sma") }}</Title>

          <SwitchField
            v-model="form.hasFacebookAdsAudience"
            :label="t('label.hasFacebookAdsAudience')"
            field-name="hasFacebookAdsAudience"
            field-id="hasFacebookAdsAudience"
          />

          <Title>{{ t("title.seo") }}</Title>

          <SwitchField
            v-model="form.hasApikomContent"
            :label="t('label.hasApikomContent')"
            field-name="hasApikomContent"
            field-id="hasApikomContent"
          />

          <SwitchField
            v-model="form.hasContentCategories"
            :label="t('label.hasContentCategories')"
            field-name="hasContentCategories"
            field-id="hasContentCategories"
          />

          <SwitchField
            v-model="form.hasContentProducts"
            :label="t('label.hasContentProducts')"
            field-name="hasContentProducts"
            field-id="hasContentProducts"
          />

          <SwitchField
            v-model="form.hasIndexation"
            :label="t('label.hasIndexation')"
            field-name="hasIndexation"
            field-id="hasIndexation"
          />

          <SwitchField
            v-model="form.hasInternalMesh"
            :label="t('label.hasInternalMesh')"
            field-name="hasInternalMesh"
            field-id="hasInternalMesh"
          />

          <SwitchField
            v-model="form.hasNetlinking"
            :label="t('label.hasNetlinking')"
            field-name="hasNetlinking"
            field-id="hasNetlinking"
          />

          <SwitchField
            v-model="form.hasPositionTracking"
            :label="t('label.hasPositionTracking')"
            field-name="hasPositionTracking"
            field-id="hasPositionTracking"
          />

          <Title>{{ t("title.sem") }}</Title>

          <SwitchField
            v-model="form.hasEmailing"
            :label="t('label.hasEmailing')"
            field-name="hasEmailing"
            field-id="hasEmailing"
          />

          <SwitchField
            v-model="form.hasShortMessageService"
            :label="t('label.hasSms')"
            field-name="hasShortMessageService"
            field-id="hasShortMessageService"
          />
        </TabPanel>

        <TabPanel>
          <ul class="flex flex-col">
            <li v-for="(module, key) in form.moduleTree" :key="key">
              <SwitchField
                v-model="module.enabled"
                :label="module.fullTitle"
                :field-name="'moduleTree[' + key + '].enabled'"
                :field-id="'moduleTree[' + key + '].enabled'"
                inline
              />
            </li>
          </ul>
        </TabPanel>

        <TabPanel>
          <ul class="flex flex-col">
            <li>
              <InputField
                v-model="form.alertCategoryWithLessThanXProductsInStock"
                :label="t('label.alertCategoryWithLessThanXProductsInStock')"
                field-name="alertCategoryWithLessThanXProductsInStock"
                field-id="alertCategoryWithLessThanXProductsInStock"
                type="number"
                placeholder="5"
                min="0"
                step="1"
              />
            </li>
          </ul>
        </TabPanel>
      </template>
    </TabGroup>

    <div
      class="flex flex-wrap items-center gap-2 rounded-b border border-gray-200 bg-white p-6"
    >
      <ActionButton type="transparent" :callback="() => goBack()">
        {{ t("button.back") }}
      </ActionButton>

      <DeleteCustomerAction
        v-if="tenant"
        :tenant="tenant"
        :callback="deleteCallback"
      >
        <template #button="{ setIsOpen }">
          <ActionButton type="secondary" :callback="() => setIsOpen(true)">
            {{ t("button.delete") }}
          </ActionButton>
        </template>
      </DeleteCustomerAction>

      <ActionButton
        submit
        :disabled="Object.keys(errors).length > 0 || isSubmitting"
      >
        <template v-if="!isSubmitting">
          {{ t("button.save") }}
        </template>

        <template v-else>
          <SpinnerIcon
            class="mr-1 size-4 animate-spin fill-white text-gray-200"
          />
          {{ t("button.saveInProgress") }}
        </template>
      </ActionButton>

      <div v-if="Object.keys(errors).length > 0" class="text-sm text-red-500">
        {{ t("error.formHasErrors") }}
      </div>
    </div>
  </Form>
</template>

<style>
#internalNotes .quill-container .ql-editor {
  height: 600px;
}
</style>
